import { useEffect, useState } from "react";
import type { FeatureFlagKey } from "@aam/shared";
import { FEATURE_FLAG_DEFINITIONS, FEATURE_FLAG_KEYS } from "@aam/shared";
import { getSessionToken } from "./useSession.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export type FeatureFlagMap = Record<FeatureFlagKey, boolean>;

function defaultFeatureFlags(): FeatureFlagMap {
  return Object.fromEntries(
    FEATURE_FLAG_KEYS.map((key) => [key, FEATURE_FLAG_DEFINITIONS[key].defaultEnabled])
  ) as FeatureFlagMap;
}

export function useFeatureFlags() {
  const token = getSessionToken();
  const [flags, setFlags] = useState<FeatureFlagMap>(defaultFeatureFlags);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!token) {
      setFlags(defaultFeatureFlags());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetch(`${API_BASE}/api/feature-flags`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || "Unable to load feature flags");
        }
        return json.data as Partial<FeatureFlagMap>;
      })
      .then((data) => {
        if (cancelled) return;
        setFlags({
          ...defaultFeatureFlags(),
          ...data,
        });
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load feature flags");
        setFlags(defaultFeatureFlags());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, reloadToken]);

  return {
    flags,
    loading,
    error,
    reload: () => setReloadToken((value) => value + 1),
  };
}
